import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification as deleteNotificationApi,
  type NotificationApi,
} from '../../api/client';
import { formatRelativeTime } from '../../utils/date';

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    getNotifications()
      .then(setNotifications)
      .catch(() => setNotifications([]))
      .finally(() => setLoading(false));
  }, []);

  const markAsRead = (id: number) => {
    markNotificationRead(id)
      .then(() => setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n))))
      .catch(() => {});
  };

  const markAllAsRead = () => {
    markAllNotificationsRead()
      .then(() => setNotifications((prev) => prev.map((n) => ({ ...n, read: true }))))
      .catch(() => {});
  };

  const deleteNotification = (id: number) => {
    deleteNotificationApi(id)
      .then(() => setNotifications((prev) => prev.filter((n) => n.id !== id)))
      .catch(() => {});
  };

  const filteredNotifications = filter === 'unread' ? notifications.filter((n) => !n.read) : notifications;
  const unreadCount = notifications.filter((n) => !n.read).length;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'music': return 'bg-orange-100 text-orange-600';
      case 'subscription': return 'bg-purple-100 text-purple-600';
      case 'social': return 'bg-pink-100 text-pink-600';
      case 'system': return 'bg-blue-100 text-blue-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-red-50">
      <div className="fixed top-14 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-orange-100 z-50">
        <div className="px-4 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full"
          >
            <i className="ri-arrow-left-line text-orange-600 text-sm" aria-hidden />
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-gray-800">Notifications</h1>
            {unreadCount > 0 && <p className="text-xs text-gray-500">{unreadCount} unread</p>}
          </div>
          <button type="button" onClick={markAllAsRead} className="text-xs text-orange-600 hover:text-orange-700">
            Mark all read
          </button>
        </div>

        <div className="px-4 pb-3">
          <div className="flex bg-gray-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${filter === 'all' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600'}`}
            >
              All ({notifications.length})
            </button>
            <button
              type="button"
              onClick={() => setFilter('unread')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${filter === 'unread' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600'}`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </div>
      </div>

      <div className="pt-44 pb-8 px-4">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-notification-off-line text-gray-400 text-2xl" aria-hidden />
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">No notifications</h3>
            <p className="text-sm text-gray-500">
              {filter === 'unread' ? 'All caught up! No unread notifications.' : 'You have no notifications yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${!notification.read ? 'border-orange-500 bg-orange-50/30' : 'border-gray-200'}`}
              >
                <div className="flex items-start">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 shrink-0 ${getTypeColor(notification.type)}`}>
                    <i className={`${notification.icon} text-sm`} aria-hidden />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="text-sm font-medium text-gray-800 truncate">{notification.title}</h4>
                      <div className="flex items-center ml-2 shrink-0">
                        {!notification.read && (
                          <button
                            type="button"
                            onClick={() => markAsRead(notification.id)}
                            className="w-6 h-6 flex items-center justify-center text-orange-600 hover:bg-orange-100 rounded-full mr-1"
                          >
                            <i className="ri-check-line text-xs" aria-hidden />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteNotification(notification.id)}
                          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-full"
                        >
                          <i className="ri-close-line text-xs" aria-hidden />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 leading-relaxed">{notification.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{formatRelativeTime(notification.time)}</span>
                      {!notification.read && <span className="w-2 h-2 bg-orange-500 rounded-full" aria-hidden />}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
